"use client";

// [ERP-ENV-VISIBILITY-BANNER-DEV-20260626-001]
// dev/prod 혼동으로 운영 DB(hitcamp_erp)에 실수로 write 하는 사고 방지용 환경 가시성 배지.
// UI(hostname/port) · API(resolved URL host) · DB(/health app_env) 환경을 표시하고,
// 불일치/위험 조합이면 빨강+pulse 로 강조. read-only(GET /health 1회), write 없음.
import { useEffect, useState } from "react";
import { getResolvedApiUrl, getHealthEnv } from "@/lib/api";

type Env = "PROD" | "DEV" | "UNKNOWN";

const PROD_HOST = "erp.hitcamp.co.kr";
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

function classifyHost(host: string, port: string): Env {
  if (host === PROD_HOST) return "PROD";
  if (LOCAL_HOSTS.has(host)) {
    if (port === "3001" || port === "8001") return "DEV";
    // 3000/8000 (운영 포트지만 로컬) 또는 기타 포트 → 단정 불가
    return "UNKNOWN";
  }
  return "UNKNOWN";
}

function detectUiEnv(): Env {
  if (typeof window === "undefined") return "UNKNOWN";
  return classifyHost(window.location.hostname, window.location.port);
}

function detectApiEnv(resolved: string): Env {
  try {
    const u = new URL(resolved);
    return classifyHost(u.hostname, u.port);
  } catch {
    return "UNKNOWN";
  }
}

function appEnvToEnv(appEnv: string | undefined): Env {
  if (appEnv === "production") return "PROD";
  if (appEnv === "development") return "DEV";
  return "UNKNOWN";
}

/** 위험 조합 판정 — UI/API/DB 중 PROD·DEV 가 섞이거나, DB 가 UNKNOWN 이면 위험. */
function isDanger(ui: Env, api: Env, db: Env): boolean {
  const vals: Env[] = [ui, api, db];
  const hasProd = vals.includes("PROD");
  const hasDev = vals.includes("DEV");
  if (hasProd && hasDev) return true; // 환경 혼재
  if (db === "UNKNOWN") return true; // write 위험 화면에서 DB 불명
  return false;
}

export default function EnvBadge({
  variant = "light",
  className = "",
}: {
  variant?: "dark" | "light";
  className?: string;
}) {
  const [mounted, setMounted] = useState(false);
  const [ui, setUi] = useState<Env>("UNKNOWN");
  const [api, setApi] = useState<Env>("UNKNOWN");
  const [db, setDb] = useState<Env>("UNKNOWN");
  const [resolvedApi, setResolvedApi] = useState<string>("");
  const [dbName, setDbName] = useState<string>("");

  useEffect(() => {
    setMounted(true);
    setUi(detectUiEnv());
    const resolved = getResolvedApiUrl();
    setResolvedApi(resolved);
    setApi(detectApiEnv(resolved));
    let alive = true;
    getHealthEnv()
      .then((h) => {
        if (!alive) return;
        setDb(appEnvToEnv(h.app_env));
        setDbName(h.db_name || "");
      })
      .catch(() => {
        if (!alive) return;
        setDb("UNKNOWN");
      });
    return () => {
      alive = false;
    };
  }, []);

  // SSR/마운트 전 — hydration mismatch 회피.
  if (!mounted) return null;

  const danger = isDanger(ui, api, db);

  // 색상: 위험=빨강+pulse / 그 외 variant 별 차분(PROD)·파랑(DEV).
  const base = "rounded-md px-2 py-1 text-[10px] font-bold leading-tight";
  let tone: string;
  if (danger) {
    tone = "animate-pulse bg-red-600 text-white border border-red-400 ring-2 ring-red-300";
  } else if (variant === "dark") {
    tone = "bg-gray-800 text-gray-200 border border-gray-600";
  } else {
    tone = "bg-gray-100 text-gray-700 border border-gray-300";
  }

  const cell = (label: string, env: Env) => {
    const color =
      env === "PROD"
        ? "text-gray-100"
        : env === "DEV"
          ? "text-indigo-300"
          : "text-amber-300";
    // light variant 에서는 어두운 텍스트로 보정(위험 시엔 흰색 유지).
    const lightColor =
      env === "PROD"
        ? "text-gray-800"
        : env === "DEV"
          ? "text-indigo-700"
          : "text-amber-700";
    const c = danger ? "text-white" : variant === "dark" ? color : lightColor;
    return (
      <span className={c}>
        {label} <span className="font-black">{env}</span>
      </span>
    );
  };

  return (
    <div
      data-testid="env-badge"
      title={`UI=${ui} | API=${api}(${resolvedApi}) | DB=${db}${dbName ? `(${dbName})` : ""}`}
      className={`${base} ${tone} ${className}`}
    >
      {danger ? (
        <div className="flex flex-col gap-0.5">
          <span>🚨 환경 불일치 주의</span>
          <span className="font-mono">
            UI {ui} · API {api} · DB {db}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 flex-wrap font-mono">
          {cell("UI", ui)}
          <span className="opacity-50">·</span>
          {cell("API", api)}
          <span className="opacity-50">·</span>
          {cell("DB", db)}
        </div>
      )}
    </div>
  );
}

/** Sidebar 접힘(w-16)용 — 색점 1개. 위험=빨강 pulse / PROD=회색 / DEV=파랑 / UNKNOWN=amber. */
export function EnvBadgeDot({ className = "" }: { className?: string }) {
  const [mounted, setMounted] = useState(false);
  const [ui, setUi] = useState<Env>("UNKNOWN");
  const [api, setApi] = useState<Env>("UNKNOWN");
  const [db, setDb] = useState<Env>("UNKNOWN");

  useEffect(() => {
    setMounted(true);
    setUi(detectUiEnv());
    setApi(detectApiEnv(getResolvedApiUrl()));
    let alive = true;
    getHealthEnv()
      .then((h) => alive && setDb(appEnvToEnv(h.app_env)))
      .catch(() => alive && setDb("UNKNOWN"));
    return () => {
      alive = false;
    };
  }, []);

  if (!mounted) return null;
  const danger = isDanger(ui, api, db);
  const dotColor = danger
    ? "bg-red-500 animate-pulse"
    : ui === "PROD"
      ? "bg-gray-400"
      : ui === "DEV"
        ? "bg-indigo-400"
        : "bg-amber-400";
  return (
    <span
      data-testid="env-badge-dot"
      title={`UI=${ui} · API=${api} · DB=${db}`}
      className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor} ${className}`}
    />
  );
}
